/* GASSISA
=========================================================================================
* Descripción: Menú de acciones del catálogo de Accesos
* Historial de cambios:
* ---------------------------------------------------------------------------------------
*    Revisión   | Fecha      | Desarrollador                    | Resumen del cambio
* ---------------------------------------------------------------------------------------
*      1       | 21/03/2026 | Lic. José Luis Aragón Cervantes  | Creación
*      2       | 31/05/2026 | Lic. José Luis Aragón Cervantes  | Protección anti doble click en acciones críticas
*      3       | 02/06/2026 | Lic. José Luis Aragón Cervantes  | Botón de eliminación de accesos
*      4       | 04/06/2026 | Lic. José Luis Aragón Cervantes  | Compatibilidad Tooltip con botones deshabilitados
*      5       | 08/06/2026 | Lic. José Luis Aragón Cervantes  | Nuevo contextual y eliminación en edición
*      6       | 08/06/2026 | Lic. José Luis Aragón Cervantes  | Separación de Nuevo y Guardar en edición
*      7       | 08/06/2026 | Lic. José Luis Aragón Cervantes  | Transformación Nuevo Guardar en un solo botón
*      8       | 08/06/2026 | Lic. José Luis Aragón Cervantes  | Eliminación solo en modo edición
* ---------------------------------------------------------------------------------------
*/

import { Fragment } from 'react';
import { Chip, IconButton, Tooltip } from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import PersonAddAltIcon from '@mui/icons-material/PersonAddAlt';
import UndoIcon from '@mui/icons-material/Undo';
import SaveIcon from '@mui/icons-material/Save';
import RefreshIcon from '@mui/icons-material/Refresh';
import EditIcon from '@mui/icons-material/Edit';
import CancelIcon from '@mui/icons-material/Cancel';
import DeleteIcon from '@mui/icons-material/Delete';

import { useAccesos } from "../../../context/Catalogos/AccesosContext";
import { useDoubleClickPrevention } from '../../../hooks/useDoubleClickPrevention';

const ActionTooltip = ({ title, children }) => (
    <Tooltip title={title}>
        <span className="tooltip-button-wrapper">
            {children}
        </span>
    </Tooltip>
);

export default function MnuAccesosActions() {
    const {
        dirty,
        HandleCargarUsuarios,
        handleNuevoUsuario,
        handleRevertirCambios,
        handleGuardarCambiosUsuario,
        handleOpenEliminarAcceso,
        handleClickRegresarMainDashboard,
        statusEnableFields,
        setStatusEnableFields,
        statusCanEdit,
        usuarioActual,
    } = useAccesos();

    const { isProcessing, executeWithProtection } = useDoubleClickPrevention();

    const handleEditar = () => {
        setStatusEnableFields(false);
    };

    const handleCancelar = () => {
        handleRevertirCambios();
        setStatusEnableFields(true);
    };

    const handleGuardar = async () => {
        const guardado = await handleGuardarCambiosUsuario();
        if (guardado) {
            setStatusEnableFields(true);
        }
    };

    const handleNuevoGuardar = async () => {
        if (statusEnableFields) {
            await handleNuevoUsuario();
            return;
        }

        await handleGuardar();
    };

    const nuevoGuardarTitle = statusEnableFields ? 'Nuevo' : 'Guardar';
    const NuevoGuardarIcon = statusEnableFields ? PersonAddAltIcon : SaveIcon;

    return (
        <Fragment>
            <ActionTooltip title={"Home"}>
                <IconButton onClick={handleClickRegresarMainDashboard}>
                    <HomeIcon className="icon" /><p className="icon">Salir</p>
                </IconButton>
            </ActionTooltip>

            <ActionTooltip title={'Recargar lista de usuarios'}>
                <IconButton onClick={HandleCargarUsuarios}>
                    <RefreshIcon className="icon" />
                </IconButton>
            </ActionTooltip>

            <ActionTooltip title={nuevoGuardarTitle}>
                <IconButton disabled={isProcessing || (statusEnableFields && dirty)} onClick={() => executeWithProtection(handleNuevoGuardar)}>
                    <NuevoGuardarIcon className="icon" /><p className="icon">{nuevoGuardarTitle}</p>
                </IconButton>
            </ActionTooltip>

            <ActionTooltip title={'Editar'}>
                <IconButton disabled={isProcessing || !statusCanEdit || !statusEnableFields} onClick={() => executeWithProtection(handleEditar)}>
                    <EditIcon className="icon" /><p className="icon">Editar</p>
                </IconButton>
            </ActionTooltip>

            <ActionTooltip title={'Eliminar acceso'}>
                <IconButton
                    disabled={isProcessing || dirty || !statusCanEdit || statusEnableFields || !usuarioActual?.id || usuarioActual?.isNuevo}
                    onClick={() => executeWithProtection(handleOpenEliminarAcceso)}
                >
                    <DeleteIcon className="icon" /><p className="icon">Eliminar</p>
                </IconButton>
            </ActionTooltip>

            <ActionTooltip title={'Revertir'}>
                <IconButton disabled={isProcessing || !dirty || statusEnableFields} onClick={() => executeWithProtection(handleRevertirCambios)}>
                    <UndoIcon className="icon" /><p className="icon">Revertir</p>
                </IconButton>
            </ActionTooltip>

            <ActionTooltip title={'Cancelar'}>
                <IconButton disabled={isProcessing || statusEnableFields} onClick={() => executeWithProtection(handleCancelar)}>
                    <CancelIcon className="icon" /><p className="icon">Cancelar</p>
                </IconButton>
            </ActionTooltip>

            {dirty && !statusEnableFields && (
                <Chip label="Cambios sin guardar" color="warning" sx={{ ml: 1 }} />
            )}
        </Fragment>
    );
}
